import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Shield, AlertTriangle, RefreshCw, X, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../../api/client';

type Severity = 'low' | 'medium' | 'high' | 'critical';
type ActionType =
  | 'mass_invite'
  | 'proxy_switch'
  | 'ban_spike'
  | 'auth_fail'
  | 'api_abuse'
  | 'account_recover'
  | 'payment_dispute';

interface RiskEvent {
  id: string;
  timestamp: string;
  user: string;
  action_type: ActionType;
  severity: Severity;
  details: string;
  ip: string;
  payload?: Record<string, unknown>;
}

interface RiskEventsResponse {
  items: RiskEvent[];
  total: number;
}

const SEVERITY_BADGE: Record<Severity, string> = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const ACTION_LABELS: Record<ActionType, string> = {
  mass_invite: 'Mass Invite',
  proxy_switch: 'Proxy Switch',
  ban_spike: 'Ban Spike',
  auth_fail: 'Auth Failure',
  api_abuse: 'API Abuse',
  account_recover: 'Account Recover',
  payment_dispute: 'Payment Dispute',
};

function SeverityBadge({ severity }: { severity: Severity }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${SEVERITY_BADGE[severity]}`}>
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
    </span>
  );
}

function DetailModal({ event, onClose }: { event: RiskEvent; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="card w-full max-w-lg mx-4 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
        >
          <X size={18} />
        </button>
        <h2 className="text-lg font-semibold mb-4">Risk Event Detail</h2>
        <dl className="space-y-2 text-sm">
          <div className="flex gap-2">
            <dt className="font-medium w-32 shrink-0 text-gray-500">ID</dt>
            <dd className="font-mono text-xs break-all">{event.id}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-medium w-32 shrink-0 text-gray-500">Timestamp</dt>
            <dd>{new Date(event.timestamp).toLocaleString()}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-medium w-32 shrink-0 text-gray-500">User</dt>
            <dd>{event.user}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-medium w-32 shrink-0 text-gray-500">Action</dt>
            <dd>{ACTION_LABELS[event.action_type]}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-medium w-32 shrink-0 text-gray-500">Severity</dt>
            <dd><SeverityBadge severity={event.severity} /></dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-medium w-32 shrink-0 text-gray-500">IP</dt>
            <dd className="font-mono">{event.ip}</dd>
          </div>
          <div className="flex gap-2">
            <dt className="font-medium w-32 shrink-0 text-gray-500">Details</dt>
            <dd>{event.details}</dd>
          </div>
          {event.payload && (
            <div>
              <dt className="font-medium text-gray-500 mb-1">Full Payload</dt>
              <dd>
                <pre className="bg-gray-50 rounded p-3 text-xs overflow-auto max-h-48">
                  {JSON.stringify(event.payload, null, 2)}
                </pre>
              </dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
}

export default function AdminRisks() {
  const [selectedEvent, setSelectedEvent] = useState<RiskEvent | null>(null);
  const [filterSeverity, setFilterSeverity] = useState<Severity | ''>('');
  const [filterAction, setFilterAction] = useState<ActionType | ''>('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [sortCol, setSortCol] = useState<keyof RiskEvent>('timestamp');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading, isFetching, refetch } = useQuery<RiskEventsResponse>({
    queryKey: ['admin-risks'],
    queryFn: () => api.get('/admin/risks').then((r) => r.data),
    refetchInterval: 60_000,
  });

  const filtered = (data?.items ?? []).filter((e) => {
    if (filterSeverity && e.severity !== filterSeverity) return false;
    if (filterAction && e.action_type !== filterAction) return false;
    if (filterEmail && !e.user.toLowerCase().includes(filterEmail.toLowerCase())) return false;
    if (filterDateFrom && e.timestamp < filterDateFrom) return false;
    if (filterDateTo && e.timestamp > filterDateTo + 'T23:59:59') return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortCol] as string;
    const bv = b[sortCol] as string;
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  function toggleSort(col: keyof RiskEvent) {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  }

  function SortIcon({ col }: { col: keyof RiskEvent }) {
    if (sortCol !== col) return null;
    return sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  }

  return (
    <div className="space-y-4">
      {selectedEvent && (
        <DetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield size={20} className="text-red-500" />
          <h1 className="text-xl font-semibold">Risk &amp; Security Panel</h1>
          {isFetching && <RefreshCw size={14} className="animate-spin text-gray-400" />}
        </div>
        <button className="btn-secondary flex items-center gap-1" onClick={() => refetch()}>
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <select
            className="input"
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value as Severity | '')}
          >
            <option value="">All severities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>

          <select
            className="input"
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value as ActionType | '')}
          >
            <option value="">All action types</option>
            {(Object.entries(ACTION_LABELS) as [ActionType, string][]).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>

          <input
            type="text"
            className="input"
            placeholder="Search email…"
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
          />

          <input
            type="date"
            className="input"
            value={filterDateFrom}
            onChange={(e) => setFilterDateFrom(e.target.value)}
          />

          <input
            type="date"
            className="input"
            value={filterDateTo}
            onChange={(e) => setFilterDateTo(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="p-8 text-center">
            <AlertTriangle size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-gray-500">No risk events detected</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {(
                    [
                      ['timestamp', 'Timestamp'],
                      ['user', 'User'],
                      ['action_type', 'Action'],
                      ['severity', 'Severity'],
                      ['details', 'Details'],
                      ['ip', 'IP'],
                    ] as [keyof RiskEvent, string][]
                  ).map(([col, label]) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left font-medium text-gray-500 cursor-pointer select-none hover:text-gray-700"
                      onClick={() => toggleSort(col)}
                    >
                      <span className="flex items-center gap-1">
                        {label}
                        <SortIcon col={col} />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {sorted.map((event) => (
                  <tr
                    key={event.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedEvent(event)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">
                      {new Date(event.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 max-w-[160px] truncate">{event.user}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {ACTION_LABELS[event.action_type]}
                    </td>
                    <td className="px-4 py-3">
                      <SeverityBadge severity={event.severity} />
                    </td>
                    <td className="px-4 py-3 max-w-[200px] truncate text-gray-600">
                      {event.details}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{event.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
